import { useState } from "react";
import {
  Alert, Box, Button, Chip, MenuItem, Paper, Stack, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { purchaseInvoicesApi, type PurchaseInvoiceListDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });
const STATUS = ["", "Draft", "Submitted", "Approved", "Posted", "Cancelled"];
const STATUS_COLOR: Record<string, "default" | "warning" | "info" | "success" | "error"> = {
  Draft: "default", Submitted: "warning", Approved: "info", Posted: "success", Cancelled: "error",
};

/**
 * UC-M1-PROC-05 — Purchase invoice list. One PI per posted GRN (enforced server-side).
 * Posted PIs link to a balanced JournalEntry (DR Inventory 1300 + DR GST 1410 / CR AP 2100).
 */
export function PurchaseInvoicesPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const canCreate = useAuth(s => s.hasPermission("procurement.pi.create"));
  const canCreateDirect = useAuth(s => s.hasPermission("procurement.pi.create-direct"));

  const query = useQuery({
    queryKey: ["purchase-invoices", status, pagination.page, pagination.pageSize],
    queryFn: () => purchaseInvoicesApi.list({
      status: status || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const columns: GridColDef<PurchaseInvoiceListDto>[] = [
    { field: "documentNo", headerName: "PI #", width: 160,
      renderCell: (p) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{p.value as string}</span>
          {p.row.source === "Direct" && (
            <Chip size="small" color="warning" variant="outlined" label="Direct" />
          )}
        </Stack>
      ) },
    { field: "documentDate", headerName: "Date", width: 110,
      valueFormatter: (v) => v ? new Date(v as string).toLocaleDateString() : "" },
    { field: "vendorName", headerName: "Vendor", flex: 1, minWidth: 200 },
    { field: "grnNo", headerName: "Linked GRN", width: 140,
      valueGetter: (_v, row) => row.grnNo ?? "—" },
    { field: "vendorInvoiceNo", headerName: "Vendor invoice", width: 160,
      valueGetter: (_v, row) => row.vendorInvoiceNo ?? "—" },
    { field: "status", headerName: "Status", width: 120,
      renderCell: (p) => <Chip size="small" label={p.value} color={STATUS_COLOR[p.value as string] ?? "default"} /> },
    { field: "totalAmount", headerName: "Total", width: 140, type: "number",
      valueFormatter: (v) => inr.format(Number(v ?? 0)) },
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Purchase invoices</Typography>
        <Stack direction="row" spacing={1}>
          {canCreateDirect && (
            <Button variant="outlined" color="warning" startIcon={<AddIcon />}
              onClick={() => navigate("/procurement/purchase-invoices/direct")}>
              New direct PI
            </Button>
          )}
          {canCreate && (
            <Button variant="contained" startIcon={<AddIcon />}
              onClick={() => navigate("/procurement/purchase-invoices/new")}>
              New PI
            </Button>
          )}
        </Stack>
      </Stack>
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}
            size="small" sx={{ minWidth: 160 }}>
            {STATUS.map((s) => <MenuItem key={s} value={s}>{s || "All"}</MenuItem>)}
          </TextField>
        </Stack>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowClick={(p) => navigate(`/procurement/purchase-invoices/${p.id}`)}
            sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
          />
        </Box>
      </Paper>
    </Box>
  );
}
